import { api } from '@smena/ui';

export interface ChannelStatus {
  kind: 'telegram_guest' | 'telegram_operator' | 'max' | 'vk' | 'webchat';
  label: string;
  configured: boolean;
  /** Короткая строка состояния. Для ботов - «Имя · @username». */
  detail?: string;
  /**
   * Имя бота, как его видит гость. Приходит из API мессенджера (getMe / getMyInfo),
   * поэтому на каждом контуре показывает бота, который тут действительно поднят.
   * Пусто, если бот не поднят или мессенджер не ответил.
   */
  botName?: string;
  /** Username бота без «@». */
  botUsername?: string;
  /** Ссылка на диалог с ботом - её УК отдаёт гостю. */
  botUrl?: string;
}

export interface IntegrationsHealth {
  icalFeeds: number;
  rcConfigured: boolean;
  rcUnmapped: number;
  /** Когда последний раз приходило событие от RC. null - ни разу. */
  rcLastEventAt: string | null;
  /** События RC, не ставшие бронью. */
  rcErrors: number;
  /** События когда-то шли, а последнюю неделю тишина - возможно, RC остановил вебхук. */
  rcSilent: boolean;
  channels: ChannelStatus[];
}

export interface IngestEvent {
  id: number;
  source: string;
  propertyId: string | null;
  bookingId: string | null;
  action: 'created' | 'updated' | 'cancelled' | 'error' | 'skipped';
  detail: Record<string, unknown>;
  ts: string;
}

export interface RcUnmappedItem {
  id: string;
  rcObjectId: string;
  nameHint: string | null;
  eventsCount: number;
  lastEventAt: string;
}

export interface RcObjectMapping {
  id: string;
  rcObjectId: string;
  propertyId: string;
  rcObjectName: string | null;
  createdAt: string;
}

export interface RcIntegration {
  id: string;
  /** Секрет из webhook-URL. В UI целиком не показываем - только внутри ссылки. */
  secret: string;
  lastEventAt: string | null;
  /** Все доставки от RC, включая не доехавшие до брони. */
  eventsCount: number;
  /** Из них стали бронью. Статус «принимаем брони» считается по нему. */
  bookingsCount: number;
  errorsCount: number;
  lastErrorAt: string | null;
  lastErrorReason: string | null;
  createdAt: string;
}

export interface RcStatus {
  /** null - интеграция не подключена: секрета и ссылки ещё (или уже) не существует. */
  integration: RcIntegration | null;
  /** Готовая ссылка для кабинета RC: API_PUBLIC_URL + ?account=&secret=. null без интеграции. */
  webhookUrl: string | null;
  mappings: RcObjectMapping[];
  unmapped: string[];
}

export async function fetchIntegrationsHealth(): Promise<IntegrationsHealth> {
  return api('/api/staff/integrations/health');
}

export async function fetchIngestEvents(): Promise<IngestEvent[]> {
  const r = await api<{ items: IngestEvent[] }>('/api/staff/integrations/events?limit=100');
  return r.items;
}

export async function fetchRcUnmapped(): Promise<RcUnmappedItem[]> {
  const r = await api<{ items: RcUnmappedItem[] }>('/api/staff/integrations/rc/unmapped');
  return r.items;
}

/** Read-only: ничего не создаёт. `integration: null` - интеграция не подключена. */
export async function fetchRcStatus(): Promise<RcStatus> {
  return api<RcStatus>('/api/staff/ingest/realtycalendar');
}

/**
 * Подключение: сервер заводит секрет и отдаёт готовую ссылку для кабинета RC.
 * Идемпотентно - на подключённой интеграции вернёт ту же ссылку, а не новую.
 */
export async function connectRc(): Promise<RcStatus> {
  return api<RcStatus>('/api/staff/ingest/realtycalendar/connect', { method: 'POST' });
}

/** Ротация секрета: старая ссылка сразу перестаёт приниматься, надо переклеить в кабинете RC. */
export async function rotateRcSecret(): Promise<RcIntegration> {
  return api<RcIntegration>('/api/staff/ingest/realtycalendar/rotate-secret', { method: 'POST' });
}

/** Фид iCal: адрес календаря площадки, который поллер обходит раз в 15 минут. */
export interface IcalFeed {
  id: string;
  /** null - агрегированный фид: объект определяется по подсказке из самого события. */
  propertyId: string | null;
  url: string;
  label: string | null;
  active: boolean;
  lastPolledAt: string | null;
  /** 'ok' | 'ok_with_errors' | 'error' - как прошёл последний обход. */
  lastStatus: string | null;
  lastError: string | null;
  lastEventsCount: number | null;
  createdAt: string;
}

/** Отдаёт фиды текущего аккаунта, включая отключённые (`active: false`). */
export async function fetchIcalFeeds(): Promise<IcalFeed[]> {
  return api<IcalFeed[]>('/api/staff/ingest/ical/feeds');
}

/** Сервер снимает `active` - поллер перестаёт обходить адрес. Уже импортированные брони остаются. */
export async function deleteIcalFeed(id: string): Promise<void> {
  await api(`/api/staff/ingest/ical/feeds/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface RcDisconnectResult {
  ok: true;
  mappingsRemoved: number;
  unmappedRemoved: number;
}

/**
 * Отключение интеграции: сносит саму интеграцию, привязки объектов и хинты «без привязки».
 * Старый вебхук после этого отбивается - брони из RC перестают доходить.
 */
export async function disconnectRc(): Promise<RcDisconnectResult> {
  return api<RcDisconnectResult>('/api/staff/ingest/realtycalendar', { method: 'DELETE' });
}

export interface CreateIcalFeedInput {
  url: string;
  label?: string | null;
  propertyId?: string | null;
}

/**
 * Добавление фида. Ручка была с самого начала, а формы в панели не было ни одной:
 * список фидов показывался, удалялся, но завести новый было нечем - в мастере настройки
 * это оставляло шаг «Источник броней» без единственного действия, ради которого он есть.
 */
export async function createIcalFeed(input: CreateIcalFeedInput): Promise<IcalFeed> {
  return api<IcalFeed>('/api/staff/ingest/ical/feeds', { method: 'POST', json: input });
}

/** Отчёт разбора файла или фида: сколько строк легло, сколько отвалилось и почему. */
export interface IngestResult {
  report: {
    inserted: number;
    updated: number;
    skipped: number;
    errors: Array<{ externalId: string | null; propertyHint: string | null; reason: string }>;
  };
  parseErrors: Array<{ row?: number; message: string }>;
  totalParsed: number;
}

export const BOOKINGS_TEMPLATE_URL = '/api/staff/ingest/template.xlsx';

/**
 * Разовая загрузка броней из CSV/XLSX. Content-Type не ставим руками: браузер сам допишет
 * boundary для multipart, а заданный вручную заголовок его затрёт и сервер не разберёт тело.
 */
export async function uploadBookingsFile(file: File): Promise<IngestResult> {
  const form = new FormData();
  form.append('file', file);
  return api<IngestResult>('/api/staff/ingest/upload', { method: 'POST', body: form });
}
